/**
 * Lazily-loaded syntax highlighting for the markdown editor's code blocks.
 *
 * The editor starts with an *empty* lowlight registry — no grammar ships in
 * the main chunk. When a code block with a known fence language mounts, its
 * NodeView calls {@link ensureGrammar}, which dynamically imports just that
 * highlight.js grammar (each is its own build chunk) and registers it. Docs
 * with no code, or only unknown languages, never load any of it.
 *
 * {@link editorLowlight} is the instance handed to `CodeBlockLowlight`. Its
 * `highlightAuto` is stubbed to return nothing: the stock fallback would
 * auto-detect unlabeled/unknown fences (` ``` ` or ` ```mermaid `) against
 * whichever grammars happen to be loaded at that moment — nondeterministic
 * coloring that changes as grammars trickle in. Unknown stays unhighlighted.
 */
import type { LanguageFn } from "highlight.js";
import { createLowlight } from "lowlight";

type GrammarLoader = () => Promise<{ default: LanguageFn }>;

// One entry per grammar chunk. Static string literals so the bundler can
// code-split each grammar; the long tail of highlight.js languages is
// intentionally not included — add entries as real docs need them.
const GRAMMAR_LOADERS: Record<string, GrammarLoader> = {
  bash: () => import("highlight.js/lib/languages/bash"),
  c: () => import("highlight.js/lib/languages/c"),
  cpp: () => import("highlight.js/lib/languages/cpp"),
  csharp: () => import("highlight.js/lib/languages/csharp"),
  css: () => import("highlight.js/lib/languages/css"),
  diff: () => import("highlight.js/lib/languages/diff"),
  dockerfile: () => import("highlight.js/lib/languages/dockerfile"),
  go: () => import("highlight.js/lib/languages/go"),
  graphql: () => import("highlight.js/lib/languages/graphql"),
  ini: () => import("highlight.js/lib/languages/ini"),
  java: () => import("highlight.js/lib/languages/java"),
  javascript: () => import("highlight.js/lib/languages/javascript"),
  json: () => import("highlight.js/lib/languages/json"),
  kotlin: () => import("highlight.js/lib/languages/kotlin"),
  less: () => import("highlight.js/lib/languages/less"),
  lua: () => import("highlight.js/lib/languages/lua"),
  makefile: () => import("highlight.js/lib/languages/makefile"),
  markdown: () => import("highlight.js/lib/languages/markdown"),
  objectivec: () => import("highlight.js/lib/languages/objectivec"),
  perl: () => import("highlight.js/lib/languages/perl"),
  php: () => import("highlight.js/lib/languages/php"),
  plaintext: () => import("highlight.js/lib/languages/plaintext"),
  python: () => import("highlight.js/lib/languages/python"),
  r: () => import("highlight.js/lib/languages/r"),
  ruby: () => import("highlight.js/lib/languages/ruby"),
  rust: () => import("highlight.js/lib/languages/rust"),
  scss: () => import("highlight.js/lib/languages/scss"),
  shell: () => import("highlight.js/lib/languages/shell"),
  sql: () => import("highlight.js/lib/languages/sql"),
  swift: () => import("highlight.js/lib/languages/swift"),
  typescript: () => import("highlight.js/lib/languages/typescript"),
  xml: () => import("highlight.js/lib/languages/xml"),
  yaml: () => import("highlight.js/lib/languages/yaml"),
};

// Fence-name → canonical grammar. Most of these are also declared inside the
// grammars themselves (hljs resolves grammar-internal aliases after
// registration); this map's job is routing the *loader* lookup, and the
// explicit registerAlias below is belt-and-suspenders for the few that aren't
// grammar-declared.
const ALIASES: Record<string, string> = {
  cjs: "javascript",
  "c++": "cpp",
  console: "shell",
  cs: "csharp",
  docker: "dockerfile",
  golang: "go",
  htm: "xml",
  html: "xml",
  js: "javascript",
  jsx: "javascript",
  kt: "kotlin",
  md: "markdown",
  mjs: "javascript",
  objc: "objectivec",
  py: "python",
  rb: "ruby",
  sh: "bash",
  svg: "xml",
  text: "plaintext",
  toml: "ini",
  ts: "typescript",
  tsx: "typescript",
  txt: "plaintext",
  yml: "yaml",
  zsh: "bash",
};

/** Canonical grammar names, for UI like the code block's language selector. */
export const SUPPORTED_LANGUAGES = Object.keys(GRAMMAR_LOADERS).sort();

function canonicalGrammar(language: string): string | null {
  const lang = language.toLowerCase();
  if (GRAMMAR_LOADERS[lang]) return lang;
  return ALIASES[lang] ?? null;
}

/**
 * Resolve a fence language (possibly an alias like `ts`) to its canonical
 * grammar name (`typescript`), or `null` when unsupported. Lets UI reflect
 * an aliased fence in terms of the canonical language list.
 */
export function canonicalLanguage(language: string): string | null {
  return canonicalGrammar(language);
}

/**
 * A lazily-populated grammar registry. The editor shares one
 * ({@link editorLowlight}); tests build their own so none sees another's grammars.
 */
export function createEditorLowlight() {
  const lowlight = createLowlight();
  const inFlight = new Map<string, Promise<boolean>>();

  /** Whether `language` (fence name or alias) can already be highlighted. */
  function grammarRegistered(language: string): boolean {
    const canon = canonicalGrammar(language);
    return canon !== null && lowlight.registered(canon);
  }

  /**
   * Load + register the grammar for a fence language. Resolves `true` once the
   * language is highlightable, `false` for languages we don't ship a grammar
   * for (or whose chunk failed to load). Concurrent calls for the same grammar
   * share one import.
   */
  function ensureGrammar(language: string): Promise<boolean> {
    const canon = canonicalGrammar(language);
    if (canon === null) return Promise.resolve(false);
    if (lowlight.registered(canon)) return Promise.resolve(true);
    let pending = inFlight.get(canon);
    if (!pending) {
      pending = GRAMMAR_LOADERS[canon]!().then(
        (mod) => {
          lowlight.register(canon, mod.default);
          const aliases = Object.keys(ALIASES).filter(
            (alias) => ALIASES[alias] === canon,
          );
          if (aliases.length) lowlight.registerAlias(canon, aliases);
          return true;
        },
        () => {
          // A failed chunk load shouldn't wedge the language forever: clear
          // the in-flight slot so a later mount can retry.
          inFlight.delete(canon);
          return false;
        },
      );
      inFlight.set(canon, pending);
    }
    return pending;
  }

  // The lowlight facade for `CodeBlockLowlight`. `highlightAuto` returns an
  // empty tree (see module docs); everything else proxies the registry.
  return {
    grammarRegistered,
    ensureGrammar,
    highlight: (language: string, text: string) =>
      lowlight.highlight(language, text),
    highlightAuto: () => ({ type: "root" as const, children: [] }),
    listLanguages: () => lowlight.listLanguages(),
    registered: (language: string) => lowlight.registered(language),
  };
}

/** The editor's shared registry, handed to `CodeBlockLowlight`. */
export const editorLowlight = createEditorLowlight();
export const { ensureGrammar, grammarRegistered } = editorLowlight;
